/* IA Offers Bar: the fixed bottom bar of timed offers.

   Liquid renders the offers whose window holds the render time. This keeps
   the bar honest after that: a card leaves at its `data-ends` epoch, the bar
   leaves with its last card, and the open/closed choice survives the visit.
   The collapsed height is published as --ia-offers-bar-height on the root
   so the page can end above the bar, and the open height as
   --ia-offers-bar-open-height so the chat launcher can clear the drawer. */

const STORAGE_KEY = 'ia-offers-bar';
const TICK_MS = 60000;

if (!customElements.get('ia-offers-bar')) {
  class IaOffersBar extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-offers-toggle]');
      this.head = this.querySelector('.ia-offers-bar__head');
      if (this.toggle && !this.toggleBound) {
        this.toggleBound = true;
        this.toggle.addEventListener('click', () => this.setOpen(!this.isOpen));
        // The whole folded head opens; only the toggle folds, and its own
        // click, which bubbles here, is the toggle's to handle.
        this.head?.addEventListener('click', (event) => {
          if (this.toggle.contains(event.target)) return;
          if (!this.isOpen) this.setOpen(true);
        });
      }
      this.setOpen(this.remembered());
      this.tick();
      this.timer = setInterval(() => this.tick(), TICK_MS);
      this.onVisible = () => { if (document.visibilityState === 'visible') this.tick(); };
      document.addEventListener('visibilitychange', this.onVisible);
      // Opening, folding, resizing and a viewport-only section crossing the
      // breakpoint all change the bar's box; the published heights follow.
      this.resizeObserver = new ResizeObserver(() => this.publishHeight());
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.onVisible);
      this.resizeObserver?.disconnect();
      this.clearHeights();
    }

    get isOpen() {
      return this.classList.contains('ia-offers-bar--open');
    }

    setOpen(open) {
      this.classList.toggle('ia-offers-bar--open', open);
      this.toggle?.setAttribute('aria-expanded', String(open));
      try {
        sessionStorage.setItem(STORAGE_KEY, open ? 'open' : 'closed');
      } catch {
        // Storage refused (private mode): the choice lasts the page.
      }
    }

    remembered() {
      try {
        return sessionStorage.getItem(STORAGE_KEY) === 'open';
      } catch {
        return false;
      }
    }

    /* Drops the cards whose window has closed; the row is a native scroll
       and needs no re-measure. */
    tick() {
      const now = Date.now() / 1000;
      this.querySelectorAll('[data-ends]').forEach((slide) => {
        if (Number(slide.dataset.ends) <= now) slide.remove();
      });
      if (!this.querySelector('[data-ends]')) {
        this.hidden = true;
        this.clearHeights();
      }
    }

    /* --ia-offers-bar-height: the head row plus the bar's padding, what the
       bar covers while closed (the page's bottom padding).
       --ia-offers-bar-open-height: the whole bar while open, so the chat
       launcher clears the drawer (ia-custom.css). Neither while hidden. */
    publishHeight() {
      if (!this.head) return;
      const styles = getComputedStyle(this);
      // Read off display: a fixed element reports no offsetParent while shown.
      if (styles.display === 'none') {
        this.clearHeights();
        return;
      }
      const root = document.documentElement.style;
      const height = this.head.offsetHeight + parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      root.setProperty('--ia-offers-bar-height', `${Math.round(height)}px`);
      if (this.isOpen) root.setProperty('--ia-offers-bar-open-height', `${this.offsetHeight}px`);
      else root.removeProperty('--ia-offers-bar-open-height');
    }

    clearHeights() {
      const root = document.documentElement.style;
      root.removeProperty('--ia-offers-bar-height');
      root.removeProperty('--ia-offers-bar-open-height');
    }
  }

  customElements.define('ia-offers-bar', IaOffersBar);
}
